/* =========================================================================
 * FINDING A GOVERNING BODY BY WHAT SOMEBODY ACTUALLY TYPES.
 *
 * Brian, live in a presentation: "in my presentation i went to try to open up
 * the PAC12 and it didnt exist."
 *
 * ⚠️ TWO SEPARATE DEFECTS PRODUCED THAT ONE SENTENCE, AND ONLY ONE OF THEM WAS
 * THE MISSING CARD.
 *
 *   1. The Pac-12 had no registry entry. Fixed in collegeAthleticBodies.js.
 *   2. THE SEARCH COULD NEVER HAVE FOUND IT EVEN WITH THE CARD THERE. The
 *      filter on GoverningBodies.jsx was a raw
 *
 *          e.acronym.toLowerCase().includes(term)
 *
 *      against `q.trim().toLowerCase()`. The stored value is "Pac-12". A person
 *      typing "PAC12", "pac 12" or "Pac.12" lowercases to "pac12", "pac 12",
 *      "pac.12" — and "pac-12" contains NONE of them. The hyphen is the whole
 *      failure. Same for "A10" vs "Atlantic 10", "B1G" vs "Big Ten", "G-MAC"
 *      vs "GMAC", "NE10" vs "Northeast-10".
 *
 * So punctuation is not part of what anyone means. The search key strips it.
 *
 * ⚠️ AND A SEARCH THAT FINDS NOTHING MUST SAY WHY, NOT SHOW A ZERO.
 * The page rendered `No association matches "PAC12"` — which is a bare zero
 * wearing a sentence. It is indistinguishable from "this conference does not
 * exist", which is exactly the conclusion Brian drew, in front of his team,
 * about a conference we hold championships for. Every other surface in this
 * codebase is already forbidden from rendering an unexplained zero
 * (`zeroKind` in bodyChampionships.js, `touchedPct: null` in assignmentViews).
 * The search box was the hole in that rule.
 *
 * `explainNoMatch` therefore returns WHICH TERM killed the result and the
 * closest bodies by name, so the answer is "nothing matches all of
 * 'ohio conference' — 'conference' is the term that excluded everything; did
 * you mean OHSAA or the Ohio Athletic Conference?" rather than silence.
 *
 * Pure. No React, no API, no I/O — same discipline as bodyMap.js beside it.
 * ========================================================================= */

import { bareOf, initialsOf, looksLikeAcronym } from './bodyMap.js'

const S = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v)).trim()

/**
 * WHAT A PERSON TYPED, REDUCED TO WHAT THEY MEANT.
 *
 * Case, accents, hyphens, spaces, periods, slashes and ampersands all go. What
 * is left is letters and digits, which is the part that carries the intent:
 *
 *   PAC12 · Pac-12 · pac 12 · Pac.12 · PAC/12   ->  pac12
 *   G-MAC · GMAC                                ->  gmac
 *   NE10  · Northeast-10                        ->  ne10 / northeast10
 *
 * ⚠️ NOT a general fuzzy match. It collapses PUNCTUATION and nothing else — no
 * stemming, no edit distance, no vowel dropping. Every previous name-matching
 * build in this repo failed by being too clever and merging distinct real
 * organisations; this one is deliberately mechanical.
 */
export const searchKey = (s) => S(s)
  .toLowerCase()
  .normalize('NFKD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, '')

/** The same reduction, but keeping word boundaries, for token matching. */
export const searchTokens = (s) => S(s)
  .toLowerCase()
  .normalize('NFKD')
  .replace(/[̀-ͯ]/g, '')
  .split(/[^a-z0-9]+/)
  .filter(Boolean)

/**
 * ⚠️ FORMER NAMES, BECAUSE A DEAD NAME IS WHY A BODY READS AS ABSENT.
 *
 * This has now cost two false reports in this codebase. The American Athletic
 * Conference rebranded to "American Conference" and a probe on the old name
 * returned ZERO — written up as a missing conference, twenty duplicate
 * championships away from being created. The WAC became the United Athletic
 * Conference on 1 July 2026 and 19 records are still filed under the dead name.
 *
 * A person searching remembers the FAMOUS name, not the current one. So the old
 * name must find the current card AND the result has to say the name changed,
 * or the same mistake gets made a third time by somebody reading a card that
 * looks unfamiliar.
 *
 * Renames only — never a guess, never a "similar" conference. Each one is
 * documented on its registry entry in collegeAthleticBodies.js.
 */
export const FORMER_NAMES = [
  { was: 'American Athletic Conference', now: 'American Conference', when: '2026' },
  { was: 'AAC', now: 'American Conference', when: '2026' },
  { was: 'Western Athletic Conference', now: 'United Athletic Conference', when: '1 July 2026' },
  { was: 'WAC', now: 'United Athletic Conference', when: '1 July 2026' },
  { was: 'Colonial Athletic Association', now: 'Coastal Athletic Association', when: '2023' },
  { was: 'ECFC', now: 'United East Conference', when: '' },
  { was: 'Commonwealth Coast Conference', now: 'Conference of New England', when: '' },
  { was: 'Capital Athletic Conference', now: 'Coast to Coast Athletic Conference', when: '' },
  { was: 'Pac-10', now: 'Pac-12 Conference', when: '2011' },
  { was: 'Pacific-12 Conference', now: 'Pac-12 Conference', when: '' },
  { was: 'Pacific-10 Conference', now: 'Pac-12 Conference', when: '2011' },
]

const FORMER_INDEX = new Map(FORMER_NAMES.map((r) => [searchKey(r.was), r]))

/** Everything about a body a person might type, as search keys. */
function haystack(e) {
  const name = S(e?.name)
  const acr = S(e?.acronym)
  return {
    name, acr,
    keys: [searchKey(name), searchKey(acr), searchKey(bareOf(name)), searchKey(bareOf(acr))].filter(Boolean),
    /* The INITIALS of the full name, so "uil" reaches "University
     * Interscholastic League" whichever way the acronym field is filled. This
     * is bodyMap's own rule, reused rather than re-derived. */
    initials: searchKey(initialsOf(name)),
    tokens: [...searchTokens(name), ...searchTokens(acr), ...searchTokens(e?.state)],
    stateKey: searchKey(e?.state),
    st: searchKey(e?.st),
    aliasKeys: (Array.isArray(e?.aliases) ? e.aliases : []).map(searchKey).filter(Boolean),
  }
}

/* Match strength, best first. The tier is what orders the results and picks the
 * suggestion — a person typing "sec" wants the SEC, not every body whose name
 * happens to contain the letters. */
const TIER = { exact: 0, former: 1, initials: 2, prefix: 3, contains: 4, tokens: 5 };

/**
 * Does this body match the query, and how strongly?
 * Returns null for no match, otherwise { tier, rank, renamed }.
 */
export function matchBody(entity, query) {
  const q = searchKey(query)
  if (!q) return { tier: 'exact', rank: 0, renamed: null }
  const h = haystack(entity)

  if (h.keys.includes(q) || h.aliasKeys.includes(q) || h.st === q) {
    return { tier: 'exact', rank: TIER.exact, renamed: null }
  }

  /* A dead name that resolves to THIS body. Carried on the result so the UI can
   * say the name changed rather than silently showing an unfamiliar card. */
  const former = FORMER_INDEX.get(q)
  if (former && h.keys.includes(searchKey(former.now))) {
    return { tier: 'former', rank: TIER.former, renamed: former }
  }

  if (h.initials && h.initials === q) return { tier: 'initials', rank: TIER.initials, renamed: null }
  if (h.keys.some((k) => k.startsWith(q))) return { tier: 'prefix', rank: TIER.prefix, renamed: null }
  if (h.keys.some((k) => k.includes(q)) || h.stateKey.includes(q)) {
    return { tier: 'contains', rank: TIER.contains, renamed: null }
  }

  /* ⚠️ EVERY TOKEN MUST MATCH, NOT ANY TOKEN — the same rule as
   * championshipSearch.js. "north carolina" meaning "north OR carolina" would
   * return every body with either word and punish the person for being
   * specific. */
  const qt = searchTokens(query)
  if (qt.length > 1 && qt.every((t) => h.tokens.some((w) => w.startsWith(t)))) {
    return { tier: 'tokens', rank: TIER.tokens, renamed: null }
  }
  return null
}

/**
 * Filter a list of registry entities by a typed query, best matches first.
 * An empty query returns the list untouched and in its original order.
 */
export function searchBodies(entities = [], query = '') {
  const q = S(query)
  if (!q) return entities.map((entity) => ({ entity, tier: 'all', rank: 0, renamed: null }))
  const out = []
  for (const entity of entities) {
    const m = matchBody(entity, q)
    if (m) out.push({ entity, ...m })
  }
  /* Stable within a tier: registry order is deliberate (public body before its
   * independents), so ties must not be reshuffled. */
  return out.sort((a, b) => a.rank - b.rank)
}

/**
 * WHY NOTHING MATCHED — never a bare zero.
 *
 * Returns { culprit, culpritReason, suggestions[] }.
 *
 * `culprit` is the single term that excluded everything: for a multi-word query
 * each term is tried alone, and a term that matches nothing by itself is the one
 * that killed the result. "ohio conference" fails on "conference" while "ohio"
 * matches four bodies — saying so turns a dead end into one more keystroke.
 *
 * `suggestions` are the closest bodies by shared leading characters on the
 * search key, so "PAC1" still offers the Pac-12 and "NCHSA" still offers
 * NCHSAA. Deliberately a prefix overlap and not an edit distance: a body that
 * shares a prefix is probably a typo of the same thing, whereas a body two
 * substitutions away is usually a different organisation, and offering those is
 * how a wrong card gets opened.
 */
export function explainNoMatch(entities = [], query = '') {
  const q = S(query)
  const terms = searchTokens(q)

  let culprit = null, culpritReason = ''
  if (terms.length > 1) {
    const counts = terms.map((t) => ({
      term: t,
      n: entities.reduce((s, e) => s + (matchBody(e, t) ? 1 : 0), 0),
    }))
    const dead = counts.filter((c) => c.n === 0)
    if (dead.length === 1) {
      culprit = dead[0].term
      const others = counts.filter((c) => c.n > 0).map((c) => `“${c.term}” matches ${c.n}`)
      culpritReason = others.length
        ? `${others.join(', ')}, but nothing matches “${culprit}”.`
        : `nothing matches “${culprit}”.`
    } else if (dead.length > 1) {
      culprit = dead.map((c) => c.term).join('” and “')
      culpritReason = `no body matches “${culprit}”.`
    } else {
      /* Every term matches something, just never the same body — which is a
       * genuinely different answer and must not be reported as a bad term. */
      culpritReason = `each of ${counts.map((c) => `“${c.term}” (${c.n})`).join(', ')} matches something, but no single body matches them all.`
    }
  } else if (terms.length === 1) {
    culpritReason = `no body’s name, acronym or state contains “${terms[0]}”, ignoring punctuation and case.`
  }

  const qk = searchKey(q)
  const shared = (a, b) => { let i = 0; while (i < a.length && i < b.length && a[i] === b[i]) i++; return i }
  const suggestions = qk
    ? entities
      .map((e) => {
        const best = Math.max(shared(qk, searchKey(e?.acronym)), shared(qk, searchKey(e?.name)))
        return { entity: e, overlap: best }
      })
      /* At least three leading characters, and at least half of what was typed,
       * or it is not a suggestion — it is noise wearing a suggestion's clothes. */
      .filter((r) => r.overlap >= 3 && r.overlap >= Math.min(3, Math.ceil(qk.length / 2)))
      .sort((a, b) => b.overlap - a.overlap || S(a.entity?.acronym).localeCompare(S(b.entity?.acronym)))
      .slice(0, 4)
    : []

  return { culprit, culpritReason, suggestions }
}

/** True when a string looks like an acronym form — re-exported so a caller does
 *  not reach past this module into bodyMap for one predicate. */
export { looksLikeAcronym }
